import template from './swag-cms-extensions-form-editor-modal.html.twig';
import './swag-cms-extensions-form-editor-modal.scss';

const { Criteria, ChangesetGenerator, ErrorResolver } = Shopware.Data;
const { mapPropertyErrors } = Shopware.Component.getComponentHelper();

export default {
    name: 'swag-cms-extensions-form-editor-modal',

    template,

    inject: [
        'SwagCmsExtensionsFormValidationService',
    ],

    mixins: [
        'cms-state',
    ],

    data() {
        return {
            activeTab: 'options',
            displayModal: true,
            showCreateTemplateModal: false,
            initiallyValidated: false,
        };
    },

    computed: {
        tabsOptions() {
            return [{
                name: 'options',
                label: this.$tc('swag-cms-extensions.sw-cms.elements.custom-form.editor.tabs.options.tabTitle')
            }, {
                name: 'fields',
                label: this.$tc('swag-cms-extensions.sw-cms.elements.custom-form.editor.tabs.fields.tabTitle')
            }];
        },

        form() {
            return Shopware.Store.get('swCmsDetailCurrentCustomForm').form;
        },

        ...mapPropertyErrors('form', [
            'technicalName',
            'title',
            'confirmationText',
            'receivers',
            'mailTemplateId',
        ]),

        technicalName: {
            set(newTechnicalName) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setFormProperty(
                    'technicalName',
                    newTechnicalName
                );
            },

            get() {
                if (this.form === null) {
                    return '';
                }

                return this.form.technicalName;
            },
        },

        mailTemplateId: {
            set(newMailTemplateId) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setFormProperty(
                    'mailTemplateId',
                    newMailTemplateId
                );
            },

            get() {
                if (this.form === null) {
                    return null;
                }

                return this.form.mailTemplateId;
            },
        },

        title: {
            set(newTitle) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setFormProperty(
                    'title',
                    newTitle,
                );
            },

            get() {
                if (this.form === null) {
                    return '';
                }

                return this.form.title;
            },
        },

        successMessage: {
            set(newSuccessMessage) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setFormProperty(
                    'successMessage',
                    newSuccessMessage,
                );
            },

            get() {
                if (this.form === null) {
                    return '';
                }

                return this.form.successMessage;
            },
        },

        receivers: {
            set(newReceivers) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setFormProperty(
                    'receivers',
                    newReceivers
                );
            },

            get() {
                if (this.form === null) {
                    return [];
                }

                return this.form.receivers;
            },
        },

        mailTemplateCriteria() {
            const criteria = new Criteria();
            criteria.addAssociation('mailTemplateType');
            criteria.addFilter(
                Criteria.equals('mailTemplateType.technicalName', 'cms_extensions.form'),
            );

            return criteria;
        },

        fieldTabHasError() {
            const fieldProperties = [
                'width',
                'type',
                'technicalName',
                'label',
                'config',
            ];

            const fieldErrorExists = fieldProperties.reduce((acc, property) => {
                if (acc) {
                    return acc;
                }

                return Shopware.Store.get('error').existsErrorInProperty(
                    'swag_cms_extensions_form_group_field',
                    property,
                );
            }, false);

            if (fieldErrorExists) {
                return true;
            }

            return Shopware.Store.get('error').existsErrorInProperty('swag_cms_extensions_form_group', 'technicalName');
        },

        optionsTabHasError() {
            return !!(this.formTechnicalNameError ||
                this.formTitleError ||
                this.formConfirmationTextError ||
                this.formReceiversError ||
                this.formMailTemplateIdError
            );
        },

        modalHasBlockingErrors() {
            return this.optionsTabHasError || this.fieldTabHasError;
        },
    },

    watch: {
        // Empty values get filtered by the DAL this watcher prevents Array function calls on null
        receivers() {
            if (Array.isArray(this.receivers)) {
                return;
            }

            this.receivers = [];
        },

        form: {
            handler() {
                if (this.initiallyValidated || !this.form) {
                    return;
                }

                if ((Shopware.Utils.object.hasOwnProperty(this.form, 'isFirstEdit') && this.form.isFirstEdit)) {
                    return;
                }

                this.validateForm().catch((error) => {
                    // The form is not valid. Add the api errors to the error state by hand.
                    this.handleErrorResponse(error);
                }).finally(() => {
                    this.initiallyValidated = true;
                });
            },
            immediate: true,
            deep: true,
        },
    },

    methods: {
        onModalClose(persist) {
            if (!persist) {
                this.displayModal = false;
                return this.$emit('modal-close', persist);
            }

            if (this.modalHasBlockingErrors) {
                this.openTabWithErrors();

                return;
            }

            if (this.form.isFirstEdit) {
                delete this.form.isFirstEdit;
            }

            // Clear previous validation errors before re-validating
            Shopware.Store.get('error').resetApiErrors();

            this.validateForm().then(() => {
                // Form is valid for the DAL. Technical names of the fields are validated by the Admin.
                // Check if a error for the technicalName of the Field still exists.
                if (Shopware.Store.get('error').existsErrorInProperty(
                    'swag_cms_extensions_form_group_field',
                    'technicalName',
                )) {
                    return;
                }

                this.displayModal = false;
                this.$emit('modal-close', persist);
            }).catch((error) => {
                this.handleErrorResponse(error);
            });
        },

        onCreateTemplate() {
            this.validateForm().then(() => {
                if (this.modalHasBlockingErrors) {
                    this.openTabWithErrors();

                    return;
                }

                if (Shopware.Store.get('error').existsErrorInProperty(
                    'swag_cms_extensions_form_group_field',
                    'technicalName',
                )) {
                    return;
                }

                this.showCreateTemplateModal = true;
            }).catch((error) => {
                this.handleErrorResponse(error);
            });
        },

        onCreateTemplateModalClose() {
            this.showCreateTemplateModal = false;
        },

        onChangeTechnicalName(newValue) {
            // If technicalName includes spaces remove all of them
            if (newValue.match(/\s/)) {
                this.form.technicalName = newValue.replaceAll(/\s/g, '');
            }

            this.$nextTick(() => {
                // Validate form to check for duplicate technical names and update errors
                this.validateForm().catch(() => {
                });
            });
        },

        validateForm() {
            const generator = new ChangesetGenerator();
            const form = Shopware.Store.get('swCmsDetailCurrentCustomForm').form;
            const changes = generator.generate(form).changes;

            if (!changes) {
                return Promise.resolve();
            }

            // For each form related entity, we add an empty translation if there are none to retrieve all errors
            if (!Shopware.Utils.object.hasOwnProperty(changes, 'translations')) {
                changes.translations = [
                    {
                        languageId: Shopware.Defaults.systemLanguageId,
                    },
                ];
            }

            if (Shopware.Utils.object.hasOwnProperty(changes, 'groups')) {
                changes.groups.forEach((group) => {
                    if (!Shopware.Utils.object.hasOwnProperty(group, 'translations')) {
                        group.translations = [
                            {
                                languageId: Shopware.Defaults.systemLanguageId,
                            },
                        ];
                    }

                    if (Shopware.Utils.object.hasOwnProperty(group, 'fields')) {
                        group.fields.forEach((field) => {
                            if (!Shopware.Utils.object.hasOwnProperty(field, 'translations')) {
                                field.translations = [
                                    {
                                        languageId: Shopware.Defaults.systemLanguageId,
                                    },
                                ];
                            }
                        });
                    }
                });
            }

            return this.SwagCmsExtensionsFormValidationService.validateForm(
                {
                    id: form.id,
                    ...changes,
                },
            ).then((response) => {
                // Try to find form with identical technicalName
                const hasIdenticalTechnicalNameForm = this.cmsPageState.currentPage.sections.some((section) => {
                    return section.blocks.some((block) => {
                        return block.slots.some((slot) => {
                            if (!Shopware.Utils.object.hasOwnProperty(slot, 'extensions') ||
                                !Shopware.Utils.object.hasOwnProperty(slot.extensions, 'swagCmsExtensionsForm')
                            ) {
                                return false;
                            }

                            // If the form we are comparing to is the current form or the names do not match return
                            return (slot.extensions.swagCmsExtensionsForm.id !== form.id
                                 && slot.extensions.swagCmsExtensionsForm.technicalName === form.technicalName);
                        });
                    });
                });

                if (!hasIdenticalTechnicalNameForm) {
                    Shopware.Store.get('error').removeApiError(
                        `swag_cms_extensions_form.${form.id}.technicalName`,
                    );

                    return Promise.resolve(response);
                }

                Shopware.Store.get('error').addApiError({
                    expression: `swag_cms_extensions_form.${form.id}.technicalName`,
                    error: {
                        id: Shopware.Utils.createId(),
                        code: 'SWAG_CUSTOM_FORM_DUPLICATE_FORM_TECHNICAL_NAME',
                        parameters: {},
                        status: '400',
                        detail: `The technical name (${form.technicalName}) is not unique in this page.`,
                    },
                });

                return Promise.reject();
            });
        },

        handleErrorResponse(error) {
            const resolver = new ErrorResolver();
            const generator = new ChangesetGenerator();
            const form = Shopware.Store.get('swCmsDetailCurrentCustomForm').form;

            resolver.handleWriteErrors(
                [{
                    entity: form,
                    changes: generator.generate(form).changes,
                }],
                error.response.data,
            );
        },

        openTabWithErrors() {
            if (this.optionsTabHasError) {
                this.activeTab = 'options';

                return;
            }

            if (this.fieldTabHasError) {
                this.activeTab = 'fields';
                this.$nextTick(() => {
                    this.selectFieldWithError();
                });
            }
        },

        selectFieldWithError() {
            if (!this.form?.groups) {
                return;
            }

            const errorStore = Shopware.Store.get('error');
            const hasErrors = (entityName, id) => {
                const errors = errorStore.getErrorsForEntity(entityName, id);

                return errors && Object.keys(errors).length > 0;
            };

            // Find first group with error
            const groupWithError = this.form.groups.find((group) => group.id && hasErrors(
                'swag_cms_extensions_form_group',
                group.id,
            ));

            if (groupWithError) {
                Shopware.Store.get('swCmsDetailCurrentCustomForm').setActiveItem(groupWithError);

                return;
            }

            // Find first field with error
            for (const group of this.form.groups) {
                if (!group.fields) {
                    continue;
                }

                const fieldWithError = group.fields.find((field) => hasErrors(
                    'swag_cms_extensions_form_group_field',
                    field.id,
                ));

                if (fieldWithError) {
                    Shopware.Store.get('swCmsDetailCurrentCustomForm').setActiveItem(fieldWithError);

                    return;
                }
            }
        },
    },
}
